import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { buildRecommender } from '../lib/recommenderShape';
import type { Profile, Recommender, UserPick } from '../types';

interface RawProfileRow {
  user_id: string;
  slug: string;
  display_name: string;
  created_at: string;
}

interface RawPickRow {
  user_id: string;
  name: string;
  place_id: string | null;
  cuisine: string | null;
  location: string | null;
  position: number;
}

function isProfileRow(value: unknown): value is RawProfileRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.user_id === 'string' &&
    typeof v.slug === 'string' &&
    typeof v.display_name === 'string' &&
    typeof v.created_at === 'string'
  );
}

function isPickRow(value: unknown): value is RawPickRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.user_id === 'string' &&
    typeof v.name === 'string' &&
    typeof v.place_id === 'string' &&
    typeof v.position === 'number'
  );
}

function rawToProfile(row: RawProfileRow): Profile {
  return {
    userId: row.user_id,
    slug: row.slug,
    displayName: row.display_name,
    createdAt: row.created_at,
  };
}

function rawToPick(row: RawPickRow): UserPick {
  return {
    name: row.name,
    placeId: row.place_id ?? '',
    cuisine: row.cuisine ?? '',
    location: row.location ?? '',
    position: row.position,
  };
}

interface UseRecommendersResult {
  recommenders: Recommender[];
  isLoaded: boolean;
  error: string | null;
}

export function useRecommenders(): UseRecommendersResult {
  const [recommenders, setRecommenders] = useState<Recommender[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const profilesQuery = supabase
        .from('profiles')
        .select('user_id, slug, display_name, created_at')
        .order('created_at', { ascending: true });
      const picksQuery = supabase
        .from('user_picks')
        .select('user_id, name, place_id, cuisine, location, position')
        .order('position', { ascending: true });

      const [profilesRes, picksRes] = await Promise.all([profilesQuery, picksQuery]);
      if (!mounted) return;

      if (profilesRes.error) {
        setError(profilesRes.error.message);
        setIsLoaded(true);
        return;
      }
      if (picksRes.error) {
        setError(picksRes.error.message);
        setIsLoaded(true);
        return;
      }

      const profiles = Array.isArray(profilesRes.data)
        ? profilesRes.data.filter(isProfileRow).map(rawToProfile)
        : [];
      const picks = Array.isArray(picksRes.data)
        ? picksRes.data.filter(isPickRow).map((row) => ({
            userId: row.user_id,
            pick: rawToPick(row),
          }))
        : [];

      const picksByUser = new Map<string, UserPick[]>();
      for (const { userId, pick } of picks) {
        const list = picksByUser.get(userId) ?? [];
        list.push(pick);
        picksByUser.set(userId, list);
      }

      setRecommenders(
        profiles.map((profile) =>
          buildRecommender(profile, picksByUser.get(profile.userId) ?? [])
        )
      );
      setIsLoaded(true);
      setError(null);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return { recommenders, isLoaded, error };
}
